import { useRef } from 'react';
import { EditIconButton } from '@/components/ui/EditIconButton';
import { formatAmount } from '@/lib/utils/formatters';
import modalStyles from '@/features/application/styles/modals.module.css';
import type {
  PaymentAdjustmentCalculatorSectionProps,
} from '../../types/modals';
import { Row } from './paymentAdjustmentModalFields';

export function PaymentAdjustmentCalculatorSection({
  tid,
  isContractPayment,
  isDefaultPayment = false,
  accessFee,
  calculation,
  adjustmentAmount,
  onAdjustmentAmountChange,
  onAdjustmentAmountBlur,
  amountTouched,
  amountError,
  isAdjustmentAmountEditable,
  isAdjustmentEditMode,
  onEnterAdjustmentEditMode,
  showRefundFeeRows = false,
}: PaymentAdjustmentCalculatorSectionProps) {
  const adjustmentInputRef = useRef<HTMLInputElement | null>(null);
  const refundFee = Number(calculation.refundFee ?? 0) || 0;
  const refundProcessingFeeAmount =
    Number(calculation.refundProcessingFeeAmount ?? 0) || 0;

  return (
    <section className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
      <h3 className="mb-4 text-base font-semibold text-slate-900">
        Payment Calculator
      </h3>
      <div className="space-y-3">
        {isDefaultPayment ? null : (
          <Row
            label={isContractPayment ? 'Access Fee' : 'App Fee'}
            value={formatAmount(accessFee)}
            isBold
          />
        )}
        <Row
          label="Starting Balance"
          value={formatAmount(calculation.startBalance)}
          isBold
        />
        {showRefundFeeRows ? (
          <Row
            label="Refund Fee"
            value={formatAmount(refundFee)}
            isBold
          />
        ) : null}
        {showRefundFeeRows && refundProcessingFeeAmount > 0 ? (
          <Row
            label="Refund Processing Fee"
            value={formatAmount(refundProcessingFeeAmount)}
            isBold
          />
        ) : null}
        <div className="space-y-1">
          <div className="flex items-center justify-between gap-4">
            <span className="text-sm font-medium text-slate-500">Adjustment</span>
            {isAdjustmentAmountEditable ? (
              <div className="relative w-36 shrink-0">
                <input
                  ref={adjustmentInputRef}
                  type="text"
                  inputMode="decimal"
                  value={adjustmentAmount}
                  readOnly={!isAdjustmentEditMode}
                  onChange={(event) => onAdjustmentAmountChange(event.target.value)}
                  onBlur={onAdjustmentAmountBlur}
                  className={`h-10 w-full rounded-xl border px-3 text-right text-sm text-slate-900 outline-none transition focus:ring-2 ${
                    amountTouched && amountError
                      ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                      : 'border-slate-200 focus:border-blue-500 focus:ring-blue-500/20'
                  } ${isAdjustmentEditMode ? 'bg-white' : 'bg-slate-50 pr-10'}`}
                  data-testid={`${tid}-calculator-adjustment-input`}
                />
                {!isAdjustmentEditMode ? (
                  <EditIconButton
                    aria-label="Edit adjustment amount"
                    title="Edit"
                    onClick={() => {
                      onEnterAdjustmentEditMode();
                      window.setTimeout(() => adjustmentInputRef.current?.focus(), 0);
                    }}
                    className="absolute right-2 top-1/2 -translate-y-1/2"
                    data-testid={`${tid}-calculator-adjustment-edit-button`}
                  />
                ) : null}
              </div>
            ) : (
              <span className="shrink-0 text-sm font-bold text-slate-900">
                {formatAmount(calculation.adjustmentPayment)}
              </span>
            )}
          </div>
          {amountTouched && amountError ? (
            <p className={`text-right ${modalStyles.formFieldError}`} role="alert">
              {amountError}
            </p>
          ) : null}
        </div>
        <Row
          label="Account Balance"
          value={formatAmount(calculation.totalBalance)}
          isBold
        />
        <Row
          label="Refund Amount"
          value={formatAmount(calculation.refundAmount)}
          isBold
        />
        <Row
          label="Balance Owed"
          value={formatAmount(calculation.balanceOwnPayment)}
          isBold
        />
      </div>
    </section>
  );
}
